/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  addDays,
  addMonths,
  isAfter,
  isBefore,
  format,
  startOfMonth,
  endOfMonth,
  parse,
  differenceInCalendarMonths,
} from 'date-fns';
import type {
  LfcFlightData,
  DatePriceObject,
  MonthPriceObject,
  CalendarPriceObject,
} from '../types';
import { SNAP_CALENDAR_PRICE_PLACEHOLDER } from '../configs/constants';

// Types re-exported for backward compatibility
// Types re-exported for backward compatibility
export type { DatePriceObject, MonthPriceObject, CalendarPriceObject };

/**
 * Parses a date string in "dd/MM/yyyy" format to a Date object.
 */
export function parseLfcDate(dateStr: string): Date | null {
  const parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date());
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
}

/**
 * Formats a Date to "dd/MM/yyyy" for the LFC API request params.
 */
export function formatDateForLfc(date: Date): string {
  return format(date, 'dd/MM/yyyy');
}

/**
 * Creates the Phoenix DatePicker date key format.
 * Must match MonthView.tsx's fixed dd/MM/yyyy lookup.
 */
function toDateKey(date: Date): string {
  return formatDateForLfc(date);
}

/**
 * Maps LFC API response data to Phoenix CalendarPriceObject format.
 *
 * Dates present in the API response → available (white):
 *   represented with a DatePriceObject containing the price.
 *
 * Dates NOT in the API response → unavailable (natural grey):
 *   represented with `noFlights: true` so the Phoenix calendar greys them out.
 *
 * @param data - The `data` array from the LFC API response
 * @param minDate - Calendar minimum date
 * @param maxDate - Calendar maximum date
 * @returns CalendarPriceObject array (one entry per month)
 */
export function mapLfcResponseToCalendarPrices(
  data: LfcFlightData[],
  minDate: Date,
  maxDate: Date,
): CalendarPriceObject {
  // Build a price lookup for quick access
  const priceLookup = new Map<string, LfcFlightData>();
  for (const item of data ?? []) {
    const date = parseLfcDate(item.departureDate);
    if (date) {
      priceLookup.set(toDateKey(date), item);
    }
  }

  const result: CalendarPriceObject = [];

  // Iterate month by month from minDate to maxDate
  let current = startOfMonth(minDate);
  const lastMonth = startOfMonth(maxDate);

  while (current <= lastMonth) {
    const monthObj: MonthPriceObject = {};
    const monthStart = current;
    const monthEnd = endOfMonth(current);

    // Iterate each day of the month
    let day = monthStart < minDate ? minDate : monthStart;
    const dayEnd = monthEnd > maxDate ? maxDate : monthEnd;

    while (day <= dayEnd) {
      const key = toDateKey(day);
      const flightData = priceLookup.get(key);

      if (flightData) {
        // Available date (fully selectable)
        monthObj[key] = {
          displayPriceString: SNAP_CALENDAR_PRICE_PLACEHOLDER,
          price: flightData.price,
          soldOut: false,
          noFlights: false,
        };
      } else {
        // No flights available
        monthObj[key] = {
          displayPriceString: SNAP_CALENDAR_PRICE_PLACEHOLDER,
          price: 0,
          soldOut: false,
          noFlights: true,
        };
      }

      day = addDays(day, 1);
    }

    result.push(monthObj);
    current = addMonths(current, 1);
    current = startOfMonth(current);
  }

  return result;
}

/**
 * Maps LFC data to CalendarPriceObject for a specific chunk, aligned to a base range.
 */
export function mapLfcChunkToCalendarPrices(
  data: LfcFlightData[],
  chunk: { beginDate: string; endDate: string },
  baseMinDate: Date,
  baseMaxDate: Date,
): CalendarPriceObject {
  const chunkStart = parseLfcDate(chunk.beginDate);
  const chunkEnd = parseLfcDate(chunk.endDate);
  if (!chunkStart || !chunkEnd) return [];

  const alignedStart = chunkStart < baseMinDate ? baseMinDate : chunkStart;
  const alignedEnd = chunkEnd > baseMaxDate ? baseMaxDate : chunkEnd;

  const monthOffset = differenceInCalendarMonths(
    startOfMonth(alignedStart),
    startOfMonth(baseMinDate),
  );

  const priceLookup = new Map<string, LfcFlightData>();
  for (const item of data ?? []) {
    const date = parseLfcDate(item.departureDate);
    if (date) {
      priceLookup.set(toDateKey(date), item);
    }
  }

  const result: CalendarPriceObject = [];

  let current = startOfMonth(alignedStart);
  const lastMonth = startOfMonth(alignedEnd);
  let monthIndex = monthOffset;

  while (current <= lastMonth) {
    const monthObj: MonthPriceObject = {};
    const monthStart = current;
    const monthEnd = endOfMonth(current);

    let day = monthStart < alignedStart ? alignedStart : monthStart;
    const dayEnd = monthEnd > alignedEnd ? alignedEnd : monthEnd;

    while (day <= dayEnd) {
      const key = toDateKey(day);
      const flightData = priceLookup.get(key);

      if (flightData) {
        monthObj[key] = {
          displayPriceString: SNAP_CALENDAR_PRICE_PLACEHOLDER,
          price: flightData.price,
          soldOut: false,
          noFlights: false,
        };
      } else {
        monthObj[key] = {
          displayPriceString: SNAP_CALENDAR_PRICE_PLACEHOLDER,
          price: 0,
          soldOut: false,
          noFlights: true,
        };
      }

      day = addDays(day, 1);
    }

    result[monthIndex] = monthObj;
    current = startOfMonth(addMonths(current, 1));
    monthIndex += 1;
  }

  return result;
}

/**
 * Calculates pre-defined chunk blocks (e.g. 3 months) that cleanly cover a date range,
 * and returns only the blocks that overlap with the currently visible months.
 *
 * @param visibleMonths - Array of [start, end] Date pairs representing active scroll views
 * @param chunkSizeMonths - Number of months each API query block should span
 * @param minDate - The baseline start date (e.g. today)
 * @param maxDate - The farthest allowable booking date
 */
export function getActiveDateChunks(
  visibleMonths: Date[][],
  chunkSizeMonths: number = 3,
  minDate: Date,
  maxDate: Date,
): { beginDate: string; endDate: string; key: string }[] {
  if (visibleMonths.length === 0) return [];

  const allChunks: { start: Date; end: Date; key: string }[] = [];
  let currentStart = startOfMonth(minDate);

  while (isBefore(currentStart, maxDate)) {
    let currentEnd = endOfMonth(addMonths(currentStart, chunkSizeMonths - 1));
    if (isAfter(currentEnd, maxDate)) {
      currentEnd = maxDate;
    }

    const key = `${format(currentStart, 'yyyy-MM')}-${format(currentEnd, 'yyyy-MM')}`;

    allChunks.push({
      start: currentStart,
      end: currentEnd,
      key,
    });

    currentStart = startOfMonth(addMonths(currentStart, chunkSizeMonths));
  }

  const activeChunks = allChunks.filter((chunk) => {
    return visibleMonths.some(([visStart, visEnd]) => {
      // Overlap condition
      return !isBefore(visEnd, chunk.start) && !isAfter(visStart, chunk.end);
    });
  });

  return activeChunks.map((chunk) => {
    // If the chunk starts before minDate, bound the API request to minDate
    const actualStart = chunk.start < minDate ? minDate : chunk.start;
    return {
      beginDate: formatDateForLfc(actualStart),
      endDate: formatDateForLfc(chunk.end),
      key: chunk.key,
    };
  });
}

/**
 * Computes the begin and end date range for the LFC API call.
 *
 * beginDate: first day of the previous month, or today (whichever is later)
 * endDate: last day of month+2 from now, or maxDate (whichever is earlier)
 */
export function computeLfcDateRange(
  referenceDate: Date,
  maxDate: Date,
): { beginDate: string; endDate: string } {
  const now = new Date();

  // Begin date: first day of previous month, or today
  const prevMonthStart = startOfMonth(addMonths(referenceDate, -1));
  const begin = prevMonthStart < now ? now : prevMonthStart;

  // End date: last day of month+2, capped at maxDate
  const futureMonthEnd = endOfMonth(addMonths(referenceDate, 2));
  const end = futureMonthEnd > maxDate ? maxDate : futureMonthEnd;

  return {
    beginDate: formatDateForLfc(begin),
    endDate: formatDateForLfc(end),
  };
}

/**
 * Merges multiple CalendarPriceObject chunks into a single array.
 */
export function mergeCalendarPriceChunks(
  chunks: CalendarPriceObject[],
): CalendarPriceObject {
  const merged: CalendarPriceObject = [];

  chunks.forEach((chunkData) => {
    (chunkData ?? []).forEach((monthObj, i) => {
      if (!merged[i]) merged[i] = {};
      if (monthObj) {
        Object.assign(merged[i], monthObj);
      }
    });
  });

  return merged;
}

/**
 * Merges two CalendarPriceObject arrays (depart + return) by intersecting availability.
 *
 * For round-trip: a date should only be shown as available if BOTH
 * depart AND return routes have availability on that date. However per the ticket,
 * the depart and return calendars are independent — so we just return them separately.
 */
export function mergeLfcPriceValues(
  departPrices: CalendarPriceObject,
  returnPrices: CalendarPriceObject,
): {
  departPriceValues: CalendarPriceObject;
  returnPriceValues: CalendarPriceObject;
} {
  return {
    departPriceValues: departPrices,
    returnPriceValues: returnPrices,
  };
}
